import React, { useState, useEffect } from 'react';
import { adService, mediaService } from '../../services/api';
import { 
  Plus, Search, Filter, Edit2, Trash2, Eye, 
  Image as ImageIcon, Globe, Settings, 
  CheckCircle, Clock, Archive, Star,
  X, Save, Loader2, DollarSign, TrendingUp,
  BarChart2, Shield, Calendar, CreditCard,
  AlertCircle, ArrowUpRight, Zap
} from 'lucide-react';

function AdsManagement() {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [activeTab, setActiveTab] = useState('ads'); // ads, packages, revenue
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    type: 'banner',
    image_url: '',
    link_url: '',
    description: '',
    position: 'homepage_main',
    priority: 0,
    start_date: '',
    end_date: '',
    status: 'active',
    package_id: '',
  });

  const [packageFormData, setPackageFormData] = useState({
    name: '',
    price: '',
    duration_days: 90,
    position: 'homepage_main',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [adsData, statsData, packagesData] = await Promise.all([
        adService.index(),
        adService.dashboard(),
        adService.packages()
      ]);
      setAds(adsData.data || adsData || []);
      setStats(statsData);
      setPackages(packagesData);
    } catch (error) {
      console.error('Error loading ads data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const res = await mediaService.upload(file, 'ads');
      setFormData(prev => ({ ...prev, image_url: res.url }));
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload image. Please check file size and format.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingAd) {
        await adService.update(editingAd.id, formData);
      } else {
        await adService.store(formData);
      }
      setShowModal(false);
      resetForm();
      loadInitialData();
      alert('Advertisement saved successfully!');
    } catch (error) {
      console.error('Error saving ad:', error);
      alert(error.response?.data?.message || 'Error saving advertisement. Please check all fields.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adService.storePackage({
        ...packageFormData,
        price: parseFloat(packageFormData.price)
      });
      setShowPackageModal(false);
      setPackageFormData({ name: '', price: '', duration_days: 90, position: 'homepage_main' });
      loadInitialData();
      alert('Ad Package created successfully!');
    } catch (error) {
      console.error('Error saving package:', error);
      alert(error.response?.data?.message || 'Error saving ad package. Ensure all fields are filled.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Approve this advertisement?')) {
      try {
        await adService.approve(id);
        loadInitialData();
      } catch (error) {
        console.error('Error approving ad:', error);
      }
    }
  };

  const handleReject = async (id) => {
    if (window.confirm('Reject this advertisement?')) {
      try {
        await adService.reject(id);
        loadInitialData();
      } catch (error) {
        console.error('Error rejecting ad:', error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ad?')) {
      try {
        await adService.destroy(id);
        loadInitialData();
      } catch (error) {
        console.error('Error deleting ad:', error);
      }
    }
  };

  const resetForm = () => {
    setEditingAd(null);
    setFormData({
      title: '',
      type: 'banner',
      image_url: '',
      link_url: '',
      description: '',
      position: 'homepage_main',
      priority: 0,
      start_date: '',
      end_date: '',
      status: 'active',
      package_id: '',
    });
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      title: ad.title || '',
      type: ad.type || 'banner',
      image_url: ad.image_url || '',
      link_url: ad.link_url || '',
      description: ad.description || '',
      position: ad.position || 'homepage_main',
      priority: ad.priority || 0,
      start_date: ad.start_date ? ad.start_date.split('T')[0] : '',
      end_date: ad.end_date ? ad.end_date.split('T')[0] : '',
      status: ad.status || 'active',
      package_id: ad.package_id || '',
    });
    setShowModal(true);
  };

  if (loading && ads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Ads & Monetization</h2>
          <p className="text-slate-400 text-sm">Manage paid advertisements, slots, and revenue.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowPackageModal(true)}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2.5 rounded-xl transition font-medium border border-slate-700"
          >
            <Settings size={18} /> Ad Packages
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl transition font-medium shadow-lg shadow-red-900/20"
          >
            <Plus size={18} /> New Advertisement
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Total Revenue</p>
              <div className="p-1.5 bg-red-500/10 rounded-lg text-red-500">
                <DollarSign size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-100">₹{parseFloat(stats.total_revenue).toLocaleString()}</p>
            <div className="flex items-center gap-1 text-[10px] text-red-500 mt-1 font-bold">
              <TrendingUp size={10} /> +12.5% this month
            </div>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Active Ads</p>
              <div className="p-1.5 bg-blue-500/10 rounded-lg text-blue-500">
                <Zap size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-100">{stats.active_ads}</p>
            <p className="text-[10px] text-slate-500 mt-1">Across all slots</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Pending Approval</p>
              <div className="p-1.5 bg-amber-500/10 rounded-lg text-amber-500">
                <Clock size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-100">{stats.pending_approval}</p>
            <p className="text-[10px] text-slate-500 mt-1">Requires your attention</p>
          </div>
          <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Avg. CTR</p>
              <div className="p-1.5 bg-purple-500/10 rounded-lg text-purple-500">
                <BarChart2 size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-100">3.8%</p>
            <p className="text-[10px] text-slate-500 mt-1">Global performance</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-slate-800 px-2">
        <button
          onClick={() => setActiveTab('ads')}
          className={`py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'ads' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          Active Campaigns
        </button>
        <button
          onClick={() => setActiveTab('packages')}
          className={`py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'packages' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          Packages & Slots
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'revenue' ? 'border-red-500 text-red-400' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}
        >
          Revenue Tracking
        </button>
      </div>

      {activeTab === 'ads' && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden animate-in fade-in duration-500">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/50 border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Advertisement</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Placement</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Performance</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {ads.map((ad) => (
                  <tr key={ad.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-10 rounded bg-slate-800 overflow-hidden shrink-0 border border-slate-700">
                          {ad.image_url ? (
                            <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-600">
                              <ImageIcon size={16} />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-100 truncate">{ad.title}</p>
                          <p className="text-[10px] text-slate-500 font-medium">By {ad.user?.name || 'Admin'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-200 capitalize">{ad.position.replace('_', ' ')}</p>
                        <p className="text-[10px] text-slate-500">{ad.type.toUpperCase()}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Views</p>
                          <p className="text-sm font-bold text-slate-100">{ad.impressions?.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Clicks</p>
                          <p className="text-sm font-bold text-red-500">{ad.clicks?.toLocaleString()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          ad.approval_status === 'approved' ? 'bg-red-500/10 text-red-500' :
                          ad.approval_status === 'rejected' ? 'bg-red-500/10 text-red-500' :
                          'bg-amber-500/10 text-amber-500'
                        }`}>
                          {ad.approval_status === 'approved' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                          {ad.approval_status}
                        </span>
                        <div className="block">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            ad.payment_status === 'paid' ? 'bg-blue-500/10 text-blue-500' : 'bg-slate-500/10 text-slate-500'
                          }`}>
                            <CreditCard size={10} />
                            {ad.payment_status}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {ad.approval_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(ad.id)}
                              className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                              title="Approve Ad"
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => handleReject(ad.id)}
                              className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                              title="Reject Ad"
                            >
                              <X size={18} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(ad)}
                          className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition"
                          title="Edit Ad"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(ad.id)}
                          className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition"
                          title="Delete Ad"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'packages' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {packages.map((pkg) => (
            <div key={pkg.id} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 relative overflow-hidden group hover:border-red-500/50 transition-all">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <Zap size={80} />
              </div>
              <p className="text-red-500 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{pkg.position.replace('_', ' ')}</p>
              <h3 className="text-xl font-black text-slate-100 mb-2">{pkg.name}</h3>
              <p className="text-slate-500 text-xs mb-6 leading-relaxed">{pkg.description || 'Standard placement duration with performance tracking included.'}</p>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-3xl font-black text-slate-100">₹{parseFloat(pkg.price).toLocaleString()}</span>
                <span className="text-slate-500 text-xs">/ {pkg.duration_days} Days</span>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle size={14} className="text-red-500" />
                  Performance Analytics
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle size={14} className="text-red-500" />
                  Multi-slot Priority
                </div>
              </div>

              <button className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-2xl transition text-sm">
                Manage Slot
              </button>
            </div>
          ))}
          <button 
            onClick={() => setShowPackageModal(true)}
            className="border-2 border-dashed border-slate-800 rounded-3xl flex flex-col items-center justify-center p-8 hover:border-red-500/50 hover:bg-red-500/5 transition-all text-slate-500 hover:text-red-500 group"
          >
            <div className="w-12 h-12 rounded-2xl bg-slate-800 group-hover:bg-red-500/20 flex items-center justify-center mb-4 transition-colors">
              <Plus size={24} />
            </div>
            <p className="font-bold">Create New Package</p>
          </button>
        </div>
      )}

      {activeTab === 'revenue' && stats && (
        <div className="space-y-6 animate-in fade-in duration-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-100 mb-6 flex items-center gap-2">
                <BarChart2 size={16} className="text-red-500" /> Revenue Forecast
              </h3>
              <div className="space-y-4">
                {stats.revenue_by_month.map((item, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                      <span className="text-slate-400">{item.month}</span>
                      <span className="text-slate-100">₹{parseFloat(item.total).toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: `${(parseFloat(item.total) / parseFloat(stats.total_revenue)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-100 mb-6 flex items-center gap-2">
                <ArrowUpRight size={16} className="text-blue-500" /> Recent Ad Payments
              </h3>
              <div className="space-y-4">
                {stats.recent_payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 rounded-2xl bg-slate-950/50 border border-slate-800 hover:border-slate-700 transition">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-500/10 rounded-xl text-red-500">
                        <CreditCard size={16} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-100">{payment.user?.name || 'External Brand'}</p>
                        <p className="text-[10px] text-slate-500 font-medium">{new Date(payment.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-black text-slate-100">₹{parseFloat(payment.amount).toLocaleString()}</p>
                      <p className="text-[10px] text-red-500 font-bold uppercase">Success</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ad Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !submitting && setShowModal(false)} />
          <div className="relative bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-100">{editingAd ? 'Edit Ad Campaign' : 'Create New Campaign'}</h3>
              <button onClick={() => !submitting && setShowModal(false)} className="p-2 text-slate-500 hover:text-slate-300 transition"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Campaign Title</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g. Summer Special 2026"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Ad Package</label>
                    <select
                      value={formData.package_id}
                      onChange={(e) => {
                        const pkg = packages.find(p => p.id === parseInt(e.target.value));
                        setFormData({ 
                          ...formData, 
                          package_id: e.target.value,
                          position: pkg ? pkg.position : formData.position
                        });
                      }}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    >
                      <option value="">No Package (Custom)</option>
                      {packages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>{pkg.name} - ₹{parseFloat(pkg.price).toLocaleString()}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-1.5">Slot Position</label>
                      <select
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-xs text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                      >
                        <option value="homepage_main">Homepage Hero</option>
                        <option value="homepage_banner">Homepage Middle</option>
                        <option value="blog_top">Blog List Top</option>
                        <option value="blog_bottom">Blog List Bottom</option>
                        <option value="blog_sidebar">Blog Sidebar</option>
                        <option value="shop_sidebar">Shop Sidebar</option>
                        <option value="category_sidebar">Category Sidebar</option>
                        <option value="product_sidebar">Product Sidebar</option>
                        <option value="product_bottom">Product Page Bottom</option>
                        <option value="brand_top">Brand Page Top</option>
                        <option value="brand_bottom">Brand Page Bottom</option>
                        <option value="public_bottom">Generic Page Bottom</option>
                        <option value="footer_top">Global Footer Top</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-300 mb-1.5">Ad Type</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-xs text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                      >
                        <option value="banner">Static Banner</option>
                        <option value="video">Video Ad</option>
                        <option value="native">Native Content</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                   <label className="block text-sm font-semibold text-slate-300 mb-1.5">Ad Asset (Image)</label>
                   <div className="relative aspect-video rounded-2xl bg-slate-950 border-2 border-dashed border-slate-800 flex flex-col items-center justify-center overflow-hidden group hover:border-red-500/50 transition-all cursor-pointer">
                    {formData.image_url ? (
                      <>
                        <img src={formData.image_url} alt="Ad Preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <p className="text-white text-xs font-bold">Replace Asset</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="text-slate-600 mb-2" size={32} />
                        <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest text-center px-4">Upload Banner Image<br/><span className="opacity-60">1200 x 400 recommended</span></p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">Redirect URL</label>
                  <div className="flex items-center">
                    <div className="p-3 bg-slate-800 border border-r-0 border-slate-800 rounded-l-xl text-slate-500">
                      <Globe size={16} />
                    </div>
                    <input
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                      placeholder="https://brand.com/campaign"
                      className="flex-1 bg-slate-950 border border-slate-800 rounded-r-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-1.5">Campaign Description (Internal)</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Notes about targeting or brand requirements..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors resize-none"
                />
              </div>
            </form>

            <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-3 bg-slate-900/50">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition font-medium"
              >
                Discard
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.title || !formData.image_url}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-2.5 rounded-xl transition font-bold shadow-lg shadow-red-900/20 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {editingAd ? 'Save Changes' : 'Start Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Package Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setShowPackageModal(false)} />
          <div className="relative bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl flex flex-col shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-100">Add Ad Package</h3>
              <button onClick={() => setShowPackageModal(false)} className="p-2 text-slate-500 hover:text-slate-300 transition"><X size={20} /></button>
            </div>
            <form onSubmit={handlePackageSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-1.5">Package Name</label>
                <input
                  type="text"
                  required
                  value={packageFormData.name}
                  onChange={(e) => setPackageFormData({ ...packageFormData, name: e.target.value })}
                  placeholder="e.g. Quarterly Hero"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">Price (₹)</label>
                  <input
                    type="number"
                    required
                    value={packageFormData.price}
                    onChange={(e) => setPackageFormData({ ...packageFormData, price: e.target.value })}
                    placeholder="9999"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-1.5">Duration (Days)</label>
                  <input
                    type="number"
                    required
                    value={packageFormData.duration_days}
                    onChange={(e) => setPackageFormData({ ...packageFormData, duration_days: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-1.5">Slot Position</label>
                <select
                  value={packageFormData.position}
                  onChange={(e) => setPackageFormData({ ...packageFormData, position: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                >
                  <option value="homepage_main">Homepage Hero</option>
                  <option value="homepage_banner">Homepage Middle</option>
                  <option value="blog_top">Blog List Top</option>
                  <option value="blog_bottom">Blog List Bottom</option>
                  <option value="blog_sidebar">Blog Sidebar</option>
                  <option value="shop_sidebar">Shop Sidebar</option>
                  <option value="category_sidebar">Category Sidebar</option>
                  <option value="product_sidebar">Product Sidebar</option>
                  <option value="product_bottom">Product Page Bottom</option>
                  <option value="brand_top">Brand Page Top</option>
                  <option value="brand_bottom">Brand Page Bottom</option>
                  <option value="public_bottom">Generic Page Bottom</option>
                  <option value="footer_top">Global Footer Top</option>
                </select>
              </div>
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl transition text-sm flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  Create Package
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdsManagement;
